import { useCallback, useContext, useEffect, useMemo, type Context } from "react";
import { isPropertyKey, type Store } from "./common";
import {
  type ConsumerQueryConfig,
  type ConsumerQueryProp,
  type ConsumerQueryResult,
  type ConsumerSetterConfig,
  type ConsumerSetterResult,
  type StoreRequest,
  type StoreValue,
} from "./types";
import { useBaseGetters } from "./useGetters";

export function makeUseConsumerQueries<
  TStoreBaseValue,
  TConsumerSetterConfig extends ConsumerSetterConfig<TStoreBaseValue>,
  TConsumerQueryConfig extends ConsumerQueryConfig<TStoreBaseValue, TConsumerSetterConfig>,
>(
  context: Context<Store<StoreValue<TStoreBaseValue>>>,
  useConsumerSetters: () => ConsumerSetterResult<TStoreBaseValue, TConsumerSetterConfig>,
  queryConfig: TConsumerQueryConfig,
) {
  return function useConsumerQueries<
    const TConsumerQueryProps extends ConsumerQueryProp<
      TStoreBaseValue,
      TConsumerSetterConfig,
      TConsumerQueryConfig
    >[],
  >(queryProps: TConsumerQueryProps) {
    const store = useContext(context);
    const consumerSetters = useConsumerSetters();

    const queries: {
      name: keyof TConsumerQueryConfig;
      alias: string;
      fn: () => Promise<StoreRequest>;
      id: string;
    }[] = useMemo(
      () =>
        queryProps.map((queryProp) => {
          const queryCall = (
            isPropertyKey(queryProp) ? { name: queryProp, args: [] } : queryProp
          ) as Exclude<typeof queryProp, PropertyKey>;

          return {
            name: queryCall.name,
            alias: (queryCall.alias ?? queryCall.name) as string,
            id:
              (queryCall.name as string) +
              "_" +
              queryCall.args.map((arg) => JSON.stringify(arg)).join(","),
            fn: () =>
              queryConfig[queryCall.name](
                { get: () => store.get().base, ...consumerSetters },
                ...queryCall.args,
              )
                .then(() => ({ status: "success" as const }))
                .catch((e) => ({
                  status: "error",
                  error: e instanceof Error ? e : new Error("Unknown error"),
                })),
          };
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [JSON.stringify(queryProps), store],
    );

    const queryKeys = queries.map((query) => query.id);

    const updateQueryRequests = useCallback(
      (storeRequest: StoreRequest[]) => {
        store.set((storeValue) => {
          queries.forEach((query, qidx) => {
            storeValue.idToRequest[query.id] = storeRequest[qidx];
          });
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [...queryKeys, store],
    );

    useEffect(() => {
      updateQueryRequests(queries.map(() => ({ status: "loading" })));

      (async () => {
        const storeRequests = await Promise.all(queries.map((query) => query.fn()));

        updateQueryRequests(storeRequests);
      })();

      return () => {
        updateQueryRequests(queries.map(() => ({ status: "idle" as const })));
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, queryKeys);

    const queryRequests = useBaseGetters(
      store,
      queries.map(
        (query) => (storeValue: StoreValue<TStoreBaseValue>) =>
          storeValue.idToRequest[query.id] ?? { status: "loading" },
      ),
    );

    return queryRequests.reduce(
      (acc, queryRequest, qidx) => ({
        ...acc,
        [queries[qidx].alias]: queryRequest,
      }),
      {},
    ) as ConsumerQueryResult<
      TStoreBaseValue,
      TConsumerSetterConfig,
      TConsumerQueryConfig,
      TConsumerQueryProps
    >;
  };
}
